// LanguageSlide — RO / EN flag picker.
//
// Sets i18next language and our localStorage locale. Doesn't gate on a
// selection — RO is preselected (the default for Go2Fix).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnboarding } from '../useOnboarding'
import SlideShell from './SlideShell'

interface LangOption {
  code: 'ro' | 'en'
  label: string
  native: string
  flag: string
}

const OPTIONS: LangOption[] = [
  { code: 'ro', label: 'Romanian', native: 'Română', flag: '🇷🇴' },
  { code: 'en', label: 'English', native: 'English', flag: '🇬🇧' },
]

interface Props {
  progress: number
}

export default function LanguageSlide({ progress }: Props) {
  const { i18n } = useTranslation()
  const { next, updateData } = useOnboarding()
  const [selected, setSelected] = useState<'ro' | 'en'>(
    (i18n.language?.startsWith('en') ? 'en' : 'ro'),
  )

  const handleContinue = () => {
    void i18n.changeLanguage(selected)
    try {
      localStorage.setItem('i18nextLng', selected)
    } catch {
      /* ignore */
    }
    updateData({ locale: selected })
    next()
  }

  return (
    <SlideShell
      progress={progress}
      footer={
        <button
          onClick={handleContinue}
          className="w-full bg-blue-600 active:bg-blue-700 active:scale-[0.99] transition text-white text-base font-medium px-4 py-3.5 rounded-xl"
        >
          Continuă
        </button>
      }
    >
      <div className="flex flex-col h-full pt-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Alege limba</h1>
          <p className="text-sm text-gray-500 mt-1">Choose your language</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {OPTIONS.map((opt) => {
            const active = selected === opt.code
            return (
              <button
                key={opt.code}
                onClick={() => setSelected(opt.code)}
                aria-pressed={active}
                className={
                  'flex flex-col items-center justify-center gap-2 p-6 rounded-2xl border-2 transition active:scale-[0.98] ' +
                  (active
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-950'
                    : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900')
                }
              >
                <span className="text-4xl" aria-hidden="true">{opt.flag}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{opt.native}</span>
                <span className="text-xs text-gray-500">{opt.label}</span>
              </button>
            )
          })}
        </div>
      </div>
    </SlideShell>
  )
}
